/**
 * Smooth scroll (Lenis)
 *
 * Reads its configuration from the JSON script tag rendered by the
 * "Smooth scroll" section, and boots a single site-wide Lenis instance.
 *
 * Exposes:
 *  - window.lenis            The active Lenis instance (or null when disabled).
 *  - 'on:smooth-scroll:init'   Fired on document when an instance is created.
 *  - 'on:smooth-scroll:destroy' Fired on document when an instance is torn down.
 */
(function () {
  if (window.themeSmoothScroll) return;

  const CONFIG_ID = 'smooth-scroll-config';
  const LOCK_CLASS = 'overflow-hidden';

  const EASINGS = {
    linear: (t) => t,
    power2: (t) => 1 - Math.pow(1 - t, 2),
    power3: (t) => 1 - Math.pow(1 - t, 3),
    power4: (t) => 1 - Math.pow(1 - t, 4)
  };

  let instance = null;
  let lockObserver = null;
  let mediaQuery = null;
  let motionQuery = null;
  let rafId = null;
  let externalRaf = false;
  let libraryLoading = false;

  function readConfig() {
    const el = document.getElementById(CONFIG_ID);
    if (!el) return null;

    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function isAllowed(config) {
    if (!config || config.enabled === false) return false;
    if (config.respectReducedMotion && motionQuery && motionQuery.matches) return false;
    if (config.disableOnMobile && mediaQuery && mediaQuery.matches) return false;
    return true;
  }

  function tick(time) {
    if (instance) instance.raf(time);
    rafId = requestAnimationFrame(tick);
  }

  function startRaf() {
    if (externalRaf || rafId !== null) return;
    rafId = requestAnimationFrame(tick);
  }

  function stopRaf() {
    if (rafId === null) return;
    cancelAnimationFrame(rafId);
    rafId = null;
  }

  /**
   * Hands the animation loop over to an external ticker (GSAP), so that
   * ScrollTrigger and Lenis update on the same frame. The caller becomes
   * responsible for calling lenis.raf().
   * @returns {boolean} True once this instance is driven externally.
   */
  function detachRaf() {
    externalRaf = true;
    stopRaf();
    return true;
  }

  function destroy() {
    stopRaf();

    if (lockObserver) {
      lockObserver.disconnect();
      lockObserver = null;
    }

    if (!instance) return;

    instance.destroy();
    instance = null;
    window.lenis = null;
    document.dispatchEvent(new CustomEvent('on:smooth-scroll:destroy'));
  }

  /**
   * Pauses Lenis whilst the theme has locked scrolling (drawers, modals, menus),
   * so those overlays keep their own native scrolling behaviour.
   */
  function watchScrollLock() {
    const sync = () => {
      if (!instance) return;
      if (document.body.classList.contains(LOCK_CLASS)) {
        instance.stop();
      } else {
        instance.start();
      }
    };

    lockObserver = new MutationObserver(sync);
    lockObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    sync();
  }

  function create(config) {
    const options = {
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      wheelMultiplier: config.wheelMultiplier,
      touchMultiplier: config.touchMultiplier,
      syncTouch: config.smoothTouch === true,
      overscroll: config.overscroll !== false,
      anchors: config.anchors !== false,
      autoRaf: false,
      allowNestedScroll: true
    };

    if (config.mode === 'lerp') {
      options.lerp = config.lerp / 100;
    } else {
      options.duration = config.duration;
      if (EASINGS[config.easing]) options.easing = EASINGS[config.easing];
    }

    instance = new window.Lenis(options);
    window.lenis = instance;

    // Keep GSAP ScrollTrigger in step when it is present on the page.
    instance.on('scroll', () => {
      if (window.ScrollTrigger) window.ScrollTrigger.update();
    });

    startRaf();
    watchScrollLock();
    document.dispatchEvent(new CustomEvent('on:smooth-scroll:init', { detail: { lenis: instance } }));
  }

  function refresh() {
    const config = readConfig();

    if (!isAllowed(config)) {
      destroy();
      return;
    }

    if (instance) return;
    if (typeof window.Lenis !== 'function') {
      if (libraryLoading) return;
      const url = document.getElementById(CONFIG_ID)?.dataset.libraryUrl;
      if (!url) return;

      libraryLoading = true;
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.onload = () => {
        libraryLoading = false;
        if (typeof window.Lenis === 'function') refresh();
      };
      script.onerror = () => {
        libraryLoading = false;
        script.remove();
        console.warn('Could not load smooth scrolling; using native scrolling.');
      };
      document.head.appendChild(script);
      return;
    }

    create(config);
  }

  function addQueryListener(query) {
    if (!query) return;
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', refresh);
    } else if (typeof query.addListener === 'function') {
      query.addListener(refresh);
    }
  }

  /**
   * Native scrolling is compositor-driven and stays smooth while the main
   * thread is still parsing scripts and decoding images. Lenis is rAF-bound,
   * so booting it during that window makes the first few seconds of scroll
   * hitch. Wait until load, then a short idle, before replacing native scroll.
   */
  function scheduleRefresh() {
    if (window.Shopify?.designMode) {
      refresh();
      return;
    }

    const start = () => refresh();

    if (document.readyState !== 'complete') {
      window.addEventListener('load', scheduleRefresh, { once: true });
      if (document.readyState !== 'complete') return;
    }

    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(start, { timeout: 800 });
    } else {
      setTimeout(start, 0);
    }
  }

  function boot() {
    const config = readConfig();
    const breakpoint = (config && config.mobileBreakpoint) || 768;

    mediaQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);
    motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    addQueryListener(mediaQuery);
    addQueryListener(motionQuery);

    scheduleRefresh();
  }

  window.themeSmoothScroll = { refresh, destroy, detachRaf, get instance() { return instance; } };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (evt) => {
    if (!evt.target.querySelector(`#${CONFIG_ID}`)) return;
    destroy();
    refresh();
  });

  document.addEventListener('shopify:section:unload', (evt) => {
    if (!evt.target.querySelector(`#${CONFIG_ID}`)) return;
    destroy();
  });
})();
